import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTalosData } from 'shared/context/TalosDataContext';
import { RARITY_ORDER, RARITY_TIER_LABELS, type RarityName } from 'features/catalog/rarity';
import './StatsCharts.css';

const pct = (part: number, whole: number): number => (whole <= 0 ? 0 : Math.round((part / whole) * 1000) / 10);

const THRESHOLD_STEP = 10;

const formatPct = (value: number): string => {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
};

type CumulativePoint = { threshold: number; count: number; share: number };

const cumulativeAtLeast = (values: number[], step: number): CumulativePoint[] => {
  const maxValue = Math.max(...values, 0);
  const n = values.length;
  const points: CumulativePoint[] = [];
  for (let threshold = 0; threshold <= maxValue; threshold += step) {
    const count = values.filter((value) => value >= threshold).length;
    points.push({
      threshold,
      count,
      share: n === 0 ? 0 : (count / n) * 100,
    });
  }
  return points;
};

type DistributionSeries = {
  key: string;
  values: number[];
  step: number;
  label: string;
  unitLabel: string;
  className: string;
};

const DistributionLine: React.FC<{
  primary: DistributionSeries;
  secondary: DistributionSeries;
  ariaLabel: string;
}> = ({ primary, secondary, ariaLabel }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setWidth(Math.floor(el.getBoundingClientRect().width));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (primary.values.length === 0) return <p className="stats-sub">No collectors yet.</p>;

  const height = 360;
  const pad = { l: 64, r: 28, t: 54, b: 36 };
  const plotW = Math.max(1, width - pad.l - pad.r);
  const plotH = height - pad.t - pad.b;
  const yMax = 100;
  const yAt = (share: number) => pad.t + (1 - share / yMax) * plotH;
  const yTicks = [0, 20, 40, 60, 80, 100];

  const layout = (series: DistributionSeries) => {
    const verts = cumulativeAtLeast(series.values, series.step);
    const xMin = verts[0]?.threshold ?? 0;
    const xMax = verts[verts.length - 1]?.threshold ?? 0;
    const xAt = (value: number) =>
      xMax === xMin ? pad.l + plotW / 2 : pad.l + ((value - xMin) / (xMax - xMin)) * plotW;
    const line = verts.map((vert) => `${xAt(vert.threshold).toFixed(1)},${yAt(vert.share).toFixed(1)}`).join(' ');
    const area = `${xAt(xMin).toFixed(1)},${yAt(0).toFixed(1)} ${line} ${xAt(xMax).toFixed(1)},${yAt(0).toFixed(1)}`;
    return { series, verts, xAt, line, area };
  };

  const bottom = layout(primary);
  const top = layout(secondary);

  const renderAxis = (axis: ReturnType<typeof layout>, side: 'top' | 'bottom') => {
    const baseY = side === 'bottom' ? pad.t + plotH : pad.t;
    const dir = side === 'bottom' ? 1 : -1;
    const ticks = axis.verts.map((vert) => vert.threshold);
    return (
      <g className={axis.series.className}>
        {ticks.map((tick, index) => (
          <g key={tick}>
            <line className="stats-line-tick" x1={axis.xAt(tick)} x2={axis.xAt(tick)} y1={baseY} y2={baseY + dir * 5} />
            <text
              className="stats-line-axis stats-line-axis--x"
              x={axis.xAt(tick)}
              y={side === 'bottom' ? baseY + 16 : baseY - 9}
              textAnchor={index === 0 ? 'start' : index === ticks.length - 1 ? 'end' : 'middle'}
            >
              {tick}+
            </text>
          </g>
        ))}
        <text
          className="stats-line-axis-title"
          x={pad.l + plotW / 2}
          y={side === 'bottom' ? height - 6 : 14}
          textAnchor="middle"
        >
          {axis.series.label}
        </text>
      </g>
    );
  };

  const renderSeries = (axis: ReturnType<typeof layout>) => (
    <g className={axis.series.className}>
      <polygon className="stats-line-area" points={axis.area} />
      <polyline className="stats-line-path" points={axis.line} />
      {axis.verts.map((vert) => (
        <circle
          key={vert.threshold}
          className="stats-line-dot"
          cx={axis.xAt(vert.threshold)}
          cy={yAt(vert.share)}
          r="3.2"
        >
          <title>
            {`${vert.threshold}+ ${axis.series.unitLabel} · ${vert.count} collector${vert.count === 1 ? '' : 's'} (${formatPct(
              vert.share
            )})`}
          </title>
        </circle>
      ))}
    </g>
  );

  return (
    <div className="stats-line-wrap" ref={wrapRef}>
      {width > 0 ? (
        <svg
          className="stats-line"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={ariaLabel}
        >
          <text
            className="stats-line-axis-title"
            x={14}
            y={pad.t + plotH / 2}
            textAnchor="middle"
            transform={`rotate(-90 14 ${pad.t + plotH / 2})`}
          >
            % of collectors
          </text>
          {bottom.verts.map((vert) => (
            <line
              key={`v-${vert.threshold}`}
              className="stats-line-grid stats-line-grid--vert"
              x1={bottom.xAt(vert.threshold)}
              x2={bottom.xAt(vert.threshold)}
              y1={pad.t}
              y2={pad.t + plotH}
            />
          ))}
          {yTicks.map((tick) => (
            <g key={tick}>
              <line className="stats-line-grid" x1={pad.l} x2={width - pad.r} y1={yAt(tick)} y2={yAt(tick)} />
              <text className="stats-line-axis" x={pad.l - 8} y={yAt(tick) + 4} textAnchor="end">
                {formatPct(tick)}
              </text>
            </g>
          ))}
          {renderSeries(top)}
          {renderSeries(bottom)}
          {renderAxis(bottom, 'bottom')}
          {renderAxis(top, 'top')}
        </svg>
      ) : null}
      <ul className="stats-legend">
        {[primary, secondary].map((series) => (
          <li key={series.key} className={series.className}>
            <span className="stats-swatch stats-line-swatch" />
            {series.label} ({series === primary ? 'bottom' : 'top'} axis)
          </li>
        ))}
      </ul>
    </div>
  );
};

export const StatsCharts: React.FC = () => {
  const { badgesMap, profilesMap } = useTalosData();

  const { rarityPointsInPlay, scores, badgeCounts } = useMemo(() => {
    const profiles = [...profilesMap.values()];
    const rarityPointsInPlay = { mythic: 0, legendary: 0, rare: 0, common: 0 };
    const badgeCounts: number[] = [];

    for (const profile of profiles) {
      let count = 0;
      for (const id of profile.badgesIDs) {
        const badge = badgesMap.get(id);
        if (!badge) continue;
        rarityPointsInPlay[badge.rarityName] += badge.rarityScore;
        count += 1;
      }
      badgeCounts.push(count);
    }

    return {
      rarityPointsInPlay,
      scores: profiles.map((profile) => profile.sortScore),
      badgeCounts,
    };
  }, [badgesMap, profilesMap]);

  const rarityParts = RARITY_ORDER.map((key) => ({
    key,
    label: RARITY_TIER_LABELS[key],
    value: rarityPointsInPlay[key as RarityName],
    className: `rarity-${key}`,
  }));
  const maxBadges = Math.max(...badgeCounts, 0);
  const badgeStep = maxBadges <= 20 ? 1 : Math.ceil(maxBadges / 20);
  const rarityTotal = rarityParts.reduce((sum, part) => sum + part.value, 0);

  return (
    <>
      <div className="stat-card">
        <h2>Where badge points come from</h2>
        <p className="stats-sub">Sum of rarity points sitting on profiles (not journeys).</p>
        <div className="stats-stack" role="img" aria-label="Where badge points come from">
          {rarityParts.map((part) =>
            part.value > 0 ? (
              <div
                key={part.key}
                className={`stats-stack-seg ${part.className}`}
                style={{ width: `${pct(part.value, rarityTotal)}%` }}
                title={`${part.label}: ${part.value} pts`}
              />
            ) : null
          )}
        </div>
        <ul className="stats-legend">
          {rarityParts.map((part) => (
            <li key={part.key}>
              <span className={`stats-swatch ${part.className}`} />
              {part.label} · {part.value} pts
              {rarityTotal > 0 ? ` (${pct(part.value, rarityTotal)}%)` : ''}
            </li>
          ))}
        </ul>
      </div>

      <div className="stat-card stats-chart-card">
        <h2>Collector distribution</h2>
        <p className="stats-sub">Share of collectors with at least this many points or badges.</p>
        <DistributionLine
          ariaLabel="Point and badge distribution as percent of collectors"
          primary={{
            key: 'points',
            values: scores,
            step: THRESHOLD_STEP,
            label: 'Points',
            unitLabel: 'pts',
            className: 'stats-series--points',
          }}
          secondary={{
            key: 'badges',
            values: badgeCounts,
            step: badgeStep,
            label: 'Badges',
            unitLabel: 'badges',
            className: 'stats-series--badges',
          }}
        />
      </div>
    </>
  );
};
